import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { io } from 'socket.io-client';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';
import Lobby from './components/Lobby';
import GameBoard from './components/GameBoard';
import { useAudio } from './contexts/AudioContext';
import './App.css';

// Check if device supports touch
const isTouchDevice = () => {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};

// Splash overlay: 首次访问时要求用户点击以解锁移动端音频
function SplashOverlay() {
  const { startBgm } = useAudio();
  const [visible, setVisible] = useState(() => !sessionStorage.getItem('splashDone'));

  const handleEnter = () => {
    startBgm(); // 必须在点击事件（用户手势）同步栈中调用
    sessionStorage.setItem('splashDone', '1');
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      onClick={handleEnter}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        background: 'rgba(8,4,1,0.92)',
        backdropFilter: 'blur(8px)',
        cursor: 'pointer',
        fontFamily: '"Noto Serif SC", serif',
        userSelect: 'none',
      }}
    >
      <div className="animate-lantern-pulse" style={{ fontSize: '4rem', marginBottom: '1.5rem' }}>🏮</div>
      <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#d4a843', letterSpacing: '0.1em', marginBottom: '0.3rem' }}>
        Phase10: Mahjong Edition
      </div>
      <div style={{ fontSize: '0.8rem', color: 'rgba(245,234,210,0.45)', letterSpacing: '0.12em', marginBottom: '1.5rem' }}>
        Vibe-coded by Martin Ma
      </div>
      <div style={{ fontSize: '0.9rem', color: 'rgba(245,234,210,0.6)', letterSpacing: '0.15em' }}>
        点击屏幕开始游戏
      </div>
    </div>
  );
}

// Determine server URL: use environment variable, or auto-detect based on current hostname
// This allows connecting from other devices on the network
const getSocketUrl = () => {
  if (process.env.REACT_APP_SOCKET_URL) {
    return process.env.REACT_APP_SOCKET_URL;
  }
  // Use the same hostname as the client but with server port 3001
  const hostname = window.location.hostname;
  const protocol = window.location.protocol === 'https:' ? 'https:' : 'http:';
  return `${protocol}//${hostname}:3001`;
};
const SOCKET_URL = getSocketUrl();

function App() {
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const [playerId, setPlayerId] = useState(null);
  const [roomCode, setRoomCode] = useState(null);
  const [room, setRoom] = useState(null);
  const [game, setGame] = useState(null);
  const [error, setError] = useState(null);
  const [cpuThinking, setCpuThinking] = useState(null);
  const [gameLogs, setGameLogs] = useState([]);
  const [gamePaused, setGamePaused] = useState(null);
  const [preloadProgress, setPreloadProgress] = useState(0);
  const [preloadComplete, setPreloadComplete] = useState(false);

  const addGameLog = useCallback((text, type = 'NORMAL', icon = '') => {
    setGameLogs(prev => {
      const newLogs = [...prev, { id: Date.now() + Math.random(), text, type, icon }];
      // Keep only last 3 logs to save memory & screen space and avoid overlap
      if (newLogs.length > 3) return newLogs.slice(newLogs.length - 3);
      return newLogs;
    });
  }, []);

  // Preload all Mahjong tile images to prevent network lag during gameplay
  useEffect(() => {
    const imagesToPreload = [
      '/mahjong/wild.png',
      '/mahjong/skip.png',
      '/mahjong/game_bg_new.png'
    ];
    for (let i = 1; i <= 34; i++) {
      imagesToPreload.push(`/mahjong/${i}.png`);
    }

    let loadedCount = 0;
    const total = imagesToPreload.length;

    // Asynchronously preload into browser cache
    imagesToPreload.forEach(src => {
      const img = new Image();
      const updateProgress = () => {
        loadedCount++;
        setPreloadProgress(Math.floor((loadedCount / total) * 100));
        if (loadedCount === total) {
          setPreloadComplete(true);
        }
      };
      img.onload = updateProgress;
      img.onerror = updateProgress;
      img.src = src;
    });
  }, []);

  // Initialize socket connection
  useEffect(() => {
    const newSocket = io(SOCKET_URL, {
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    newSocket.on('connect', () => {
      console.log('Connected to server');
      setConnected(true);
      setError(null);
    });

    newSocket.on('disconnect', () => {
      console.log('Disconnected from server');
      setConnected(false);
    });

    newSocket.on('connect_error', (err) => {
      console.error('Connection error:', err);
      setError('Unable to connect to server. Please try again.');
    });

    setSocket(newSocket);

    return () => {
      newSocket.close();
    };
  }, []);

  // Socket event handlers
  useEffect(() => {
    if (!socket) return;

    // Room events
    socket.on('roomUpdated', ({ room }) => {
      setRoom(room);
    });

    socket.on('playerJoined', ({ player, room }) => {
      setRoom(room);
    });

    socket.on('playerLeft', ({ playerId: leftId, room, newHostId }) => {
      setRoom(room);
    });

    socket.on('playerDisconnected', ({ playerId: disconnectedId, room }) => {
      if (room) setRoom(room);
    });

    // Game events
    socket.on('gameStarted', ({ game }) => {
      setGame(game);
    });

    socket.on('gameUpdated', ({ game }) => {
      setGame(game);
      setCpuThinking(null);
    });

    socket.on('cpuThinking', ({ playerId }) => {
      setCpuThinking(playerId);
    });

    socket.on('cpuAction', ({ playerId, action }) => {
      // Handle CPU action display if needed
    });

    socket.on('roundEnded', ({ roundWinnerId }) => {
      // Round ended - game state already updated
    });

    socket.on('gameEnded', ({ winnerId }) => {
      // Game ended - game state already updated
    });

    socket.on('newRoundStarted', ({ roundNumber }) => {
      setCpuThinking(null);
    });

    socket.on('playerSkipped', ({ skippedPlayerId, skippedPlayerName }) => {
      const name = skippedPlayerName.split(' ')[0];
      addGameLog(`${name} was skipped`, 'IMPORTANT');
    });

    socket.on('deckExhausted', ({ message }) => {
      addGameLog(message, 'IMPORTANT');
    });

    socket.on('playerHit', ({ hittingPlayerId, hittingPlayerName, targetPlayerId, targetPlayerName, card }) => {
      const name = hittingPlayerName.split(' ')[0];
      const target = targetPlayerName.split(' ')[0];
      const cardType = card?.type === 'wild' ? 'Wild' : card?.type === 'number' ? `${card.color} ${card.value}` : 'Card';
      addGameLog(`${name} added ${cardType} to ${target}'s phase`, 'NORMAL');
    });

    socket.on('playerPhasedOut', ({ playerId, playerName, phaseNumber }) => {
      const name = playerName.split(' ')[0];
      addGameLog(`${name} cleared Phase ${phaseNumber}!`, 'IMPORTANT');
    });

    socket.on('playerDrew', ({ playerId, playerName, source, card }) => {
      const name = playerName.split(' ')[0];
      if (source === 'deck') {
        addGameLog(`${name} drew a tile`, 'NORMAL');
      } else {
        const cardType = card?.type === 'wild' ? 'Wild' : card?.type === 'skip' ? 'Skip' : `${card.color} ${card.value}`;
        addGameLog(`${name} claimed ${cardType}`, 'NORMAL');
      }
    });

    // Pause/Resume events
    socket.on('gamePaused', ({ pausedByPlayerId, pausedByPlayerName, reason }) => {
      setGamePaused({ pausedByPlayerId, pausedByPlayerName, reason });
    });

    socket.on('gameResumed', ({ resumedByPlayerId, resumedByPlayerName }) => {
      setGamePaused(null);
    });

    // Rematch event - go back to lobby
    socket.on('rematchStarted', ({ room }) => {
      setRoom(room);
      setGame(null);
      setGamePaused(null);
    });

    // Player reconnection
    socket.on('playerReconnected', ({ playerId: reconnectedId, playerName }) => {
      // Could show a notification here
    });

    return () => {
      socket.off('roomUpdated');
      socket.off('playerJoined');
      socket.off('playerLeft');
      socket.off('playerDisconnected');
      socket.off('gameStarted');
      socket.off('gameUpdated');
      socket.off('cpuThinking');
      socket.off('cpuAction');
      socket.off('roundEnded');
      socket.off('gameEnded');
      socket.off('newRoundStarted');
      socket.off('playerSkipped');
      socket.off('deckExhausted');
      socket.off('playerHit');
      socket.off('playerPhasedOut');
      socket.off('playerDrew');
      socket.off('gamePaused');
      socket.off('gameResumed');
      socket.off('rematchStarted');
      socket.off('playerReconnected');
    };
  }, [socket, addGameLog]);

  // Create room handler
  const handleCreateRoom = useCallback((name, settings) => {
    if (!socket) return;

    socket.emit('createRoom', { playerName: name, settings }, (response) => {
      if (response.success) {
        setPlayerId(response.playerId);
        setRoomCode(response.roomCode);
        setRoom(response.room);
        setError(null);
      } else {
        setError(response.error);
      }
    });
  }, [socket]);

  // Join room handler
  const handleJoinRoom = useCallback((name, code) => {
    if (!socket) return;

    socket.emit('joinRoom', { roomCode: code, playerName: name }, (response) => {
      if (response.success) {
        setPlayerId(response.playerId);
        setRoomCode(code.toUpperCase());
        setRoom(response.room);
        setError(null);
      } else {
        setError(response.error);
      }
    });
  }, [socket]);

  // Add CPU handler
  const handleAddCPU = useCallback(() => {
    if (!socket || !roomCode) return;

    socket.emit('addCPU', { roomCode }, (response) => {
      if (response.success) {
        setRoom(response.room);
      } else {
        setError(response.error);
      }
    });
  }, [socket, roomCode]);

  // Remove CPU handler
  const handleRemoveCPU = useCallback((cpuId) => {
    if (!socket || !roomCode) return;

    socket.emit('removeCPU', { roomCode, cpuId }, (response) => {
      if (response.success) {
        setRoom(response.room);
      } else {
        setError(response.error);
      }
    });
  }, [socket, roomCode]);

  // Update settings handler
  const handleUpdateSettings = useCallback((settings) => {
    if (!socket || !roomCode) return;

    socket.emit('updateSettings', { roomCode, settings }, (response) => {
      if (response.success) {
        setRoom(response.room);
      } else {
        setError(response.error);
      }
    });
  }, [socket, roomCode]);

  // Leave room handler
  const handleLeaveRoom = useCallback(() => {
    if (!socket || !roomCode) return;

    socket.emit('leaveRoom', { roomCode }, (response) => {
      if (response.success) {
        setRoomCode(null);
        setRoom(null);
        setGame(null);
        setPlayerId(null);
      } else {
        setError(response.error);
      }
    });
  }, [socket, roomCode]);

  // Start game handler
  const handleStartGame = useCallback(() => {
    if (!socket || !roomCode) return;

    socket.emit('startGame', { roomCode }, (response) => {
      if (!response.success) {
        setError(response.error);
      }
    });
  }, [socket, roomCode]);

  // Game action handlers
  const handleDrawCard = useCallback((source) => {
    if (!socket || !roomCode) return Promise.reject('Not connected');

    return new Promise((resolve, reject) => {
      socket.emit('drawCard', { roomCode, source }, (response) => {
        if (response.success) {
          resolve(response);
        } else {
          reject(response.error);
        }
      });
    });
  }, [socket, roomCode]);

  const handleLayDownPhase = useCallback((cardGroups) => {
    if (!socket || !roomCode) return Promise.reject('Not connected');

    return new Promise((resolve, reject) => {
      socket.emit('layDownPhase', { roomCode, cardGroups }, (response) => {
        if (response.success) {
          resolve(response);
        } else {
          reject(response.error);
        }
      });
    });
  }, [socket, roomCode]);

  const handleHitCard = useCallback((targetPlayerId, cardId, groupIndex, position = null) => {
    if (!socket || !roomCode) return Promise.reject('Not connected');

    return new Promise((resolve, reject) => {
      socket.emit('hitCard', { roomCode, targetPlayerId, cardId, groupIndex, position }, (response) => {
        if (response.success) {
          resolve(response);
        } else {
          reject(response.error);
        }
      });
    });
  }, [socket, roomCode]);

  const handleDiscardCard = useCallback((cardId, targetPlayerId = null) => {
    if (!socket || !roomCode) return Promise.reject('Not connected');

    return new Promise((resolve, reject) => {
      socket.emit('discardCard', { roomCode, cardId, targetPlayerId }, (response) => {
        if (response.success) {
          resolve(response);
        } else {
          reject(response.error);
        }
      });
    });
  }, [socket, roomCode]);

  const handleSelectPhase = useCallback((phaseNumber) => {
    if (!socket || !roomCode) return Promise.reject('Not connected');

    return new Promise((resolve, reject) => {
      socket.emit('selectPhase', { roomCode, phaseNumber }, (response) => {
        if (response.success) {
          resolve(response);
        } else {
          reject(response.error);
        }
      });
    });
  }, [socket, roomCode]);

  const handleStartNewRound = useCallback(() => {
    if (!socket || !roomCode) return Promise.reject('Not connected');

    return new Promise((resolve, reject) => {
      socket.emit('startNewRound', { roomCode }, (response) => {
        if (response.success) {
          resolve(response);
        } else {
          reject(response.error);
        }
      });
    });
  }, [socket, roomCode]);

  // Rematch handler - reset game and go back to lobby
  const handleRematch = useCallback(() => {
    if (!socket || !roomCode) return Promise.reject('Not connected');

    return new Promise((resolve, reject) => {
      socket.emit('rematch', { roomCode }, (response) => {
        if (response.success) {
          resolve(response);
        } else {
          reject(response.error);
        }
      });
    });
  }, [socket, roomCode]);

  // Clear error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Select backend based on device type
  // Touch backend with no delay for immediate drag response on mobile
  const dndBackend = useMemo(() => isTouchDevice() ? TouchBackend : HTML5Backend, []);
  const dndOptions = useMemo(() => isTouchDevice() ? {
    enableMouseEvents: true,
    delayTouchStart: 0,  // No delay - drag starts immediately on touch
    delayMouseStart: 0,
  } : undefined, []);

  return (
    <DndProvider backend={dndBackend} options={dndOptions}>
      <SplashOverlay />
      <div
         className="min-h-screen bg-game-bg"
         style={{ color: '#f5ead2' }}
      >

        {/* Error Toast */}
        {error && (
          <div
            className="fixed top-4 right-4 z-50 animate-slide-in px-5 py-3 rounded"
            style={{
              background: 'linear-gradient(135deg, #7a1f1f, #c0392b)',
              border: '1px solid rgba(212,168,67,0.5)',
              color: '#f5ead2',
              fontFamily: '"Noto Serif SC", serif',
              boxShadow: '0 4px 20px rgba(192,57,43,0.5), 0 0 0 1px rgba(0,0,0,0.5)',
            }}
          >
            ⚠ {error}
          </div>
        )}

        {/* Connection status */}
        {!connected && (
          <div
            className="fixed top-4 left-4 z-50 flex items-center gap-2 px-4 py-2 rounded"
            style={{
              background: 'rgba(13,31,20,0.9)',
              border: '1px solid rgba(212,168,67,0.4)',
              color: '#d4a843',
              fontFamily: '"Cinzel", serif',
              fontSize: '0.8rem',
              letterSpacing: '0.08em',
            }}
          >
            <span className="animate-lantern-pulse" style={{ fontSize: '1rem' }}>🏮</span>
            Connecting...
          </div>
        )}

        {/* Main content */}
        {!game ? (
          <Lobby
            connected={connected}
            playerId={playerId}
            roomCode={roomCode}
            room={room}
            onCreateRoom={handleCreateRoom}
            onJoinRoom={handleJoinRoom}
            onAddCPU={handleAddCPU}
            onRemoveCPU={handleRemoveCPU}
            onUpdateSettings={handleUpdateSettings}
            onLeaveRoom={handleLeaveRoom}
            onStartGame={handleStartGame}
            preloadProgress={preloadProgress}
            preloadComplete={preloadComplete}
          />
        ) : !preloadComplete ? (
          <div className="absolute inset-0 z-50 flex flex-col items-center justify-center p-4 text-center" style={{ background: 'rgba(10,6,2,0.98)', fontFamily: '"Cinzel", serif' }}>
            <div className="text-xl sm:text-2xl mb-6 font-bold" style={{ color: '#d4a843', letterSpacing: '0.15em' }}>
              LOADING GAME ASSETS
            </div>
            <div className="w-full max-w-md h-2 bg-gray-800 rounded-full overflow-hidden shadow-inner">
              <div 
                className="h-full transition-all duration-300 rounded-full" 
                style={{ width: `${preloadProgress}%`, background: 'linear-gradient(90deg, #d4a843, #f5ead2)', boxShadow: '0 0 10px rgba(212,168,67,0.5)' }} 
              />
            </div>
            <div className="mt-4 text-sm" style={{ color: 'rgba(245,234,210,0.6)', letterSpacing: '0.1em' }}>
              {preloadProgress}%
            </div>
          </div>
        ) : (
          <GameBoard
            game={game}
            playerId={playerId}
            roomCode={roomCode}
            room={room}
            cpuThinking={cpuThinking}
            gameLogs={gameLogs}
            gamePaused={gamePaused}
            onDrawCard={handleDrawCard}
            onLayDownPhase={handleLayDownPhase}
            onHitCard={handleHitCard}
            onDiscardCard={handleDiscardCard}
            onSelectPhase={handleSelectPhase}
            onStartNewRound={handleStartNewRound}
            onRematch={handleRematch}
            onLeaveRoom={handleLeaveRoom}
          />
        )}
      </div>
    </DndProvider>
  );
}

export default App;
